import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import {
    caretAltToLeftIcon,
    caretAltLeftIcon,
    caretAltRightIcon,
    caretAltToRightIcon,
} from '@progress/kendo-svg-icons';

/**
 * Custom Grid pager that replicates the public surface of Kendo's
 * `<Pager>` from `@progress/kendo-react-data-tools` but renders its
 * own `<DropDownList>` for the page-size dropdown so we can pass
 * `popupSettings.popupClass: 'is-pager-rpp'`. The popup then carries
 * that class from the first paint — CSS in `plugin.k-list.css`
 * scopes the native `<select>`-look styling and the instant
 * open/close transitions to it without runtime DOM tagging, body
 * `:has()` tricks, or the close-animation snap that comes from
 * aria-state-based scoping.
 *
 * Why not use Kendo's `<Pager>` and just inject a class via the
 * `pager` prop? Kendo's Pager creates its page-size DropDownList via
 * the internal `PagerPageSizes` component (`@hidden`, not exported,
 * blocked by the package's `exports` field) and passes only `value`,
 * `data`, `onChange`, `ariaLabel`, `size`, `tabIndex`, `adaptive`,
 * `adaptiveTitle`. There is no `popupSettings` or `popupClass` hook,
 * so the popup is uncustomizable without runtime DOM tagging.
 *
 * Markup matches Kendo's pager so the app's `.k-grid-pager` /
 * `.k-pager-numbers-wrap` / `.k-pager-sizes` / `.k-pager-info` CSS
 * keeps working unchanged.
 */
const PagerTemplate = ({
    skip,
    take,
    total,
    buttonCount = 10,
    info = true,
    pageSizes,
    previousNext = true,
    size,
    onPageChange,
}) => {
    const safeTake = take > 0 ? take : 1;
    const currentPage = Math.floor(skip / safeTake) + 1;
    const totalPages = Math.max(1, Math.ceil((total || 0) / safeTake));

    const goToPage = useCallback((page, e) => {
        if (!onPageChange || page < 1 || page > totalPages || page === currentPage) return;
        onPageChange({
            skip: (page - 1) * take,
            take,
            syntheticEvent: e,
            nativeEvent: e && e.nativeEvent,
            targetEvent: e,
        });
    }, [onPageChange, take, totalPages, currentPage]);

    const handlePageSizeChange = useCallback((e) => {
        if (!onPageChange) return;
        onPageChange({
            skip: 0,
            take: e.target.value,
            syntheticEvent: e.syntheticEvent,
            nativeEvent: e.nativeEvent,
            targetEvent: e,
        });
    }, [onPageChange]);

    // Kendo's numeric pager shows pages in fixed chunks of `buttonCount`.
    // When the current page crosses a chunk boundary the window slides.
    const chunkStart = Math.floor((currentPage - 1) / buttonCount) * buttonCount + 1;
    const chunkEnd = Math.min(chunkStart + buttonCount - 1, totalPages);
    const showStartEllipsis = chunkStart > 1;
    const showEndEllipsis = chunkEnd < totalPages;

    const numericButtons = [];
    for (let p = chunkStart; p <= chunkEnd; p++) {
        const isSelected = p === currentPage;
        numericButtons.push(
            <Button
                key={p}
                fillMode="flat"
                size={size}
                themeColor={isSelected ? 'primary' : undefined}
                className={`k-button-flat k-pager-nav k-pager-number${isSelected ? ' k-selected' : ''}`}
                title={`Page ${p}`}
                aria-label={`Page ${p}`}
                aria-current={isSelected ? 'page' : undefined}
                onClick={(e) => { e.preventDefault(); goToPage(p, e); }}
            >
                {p}
            </Button>
        );
    }

    const navBtn = (page, title, icon, disabled, extraClass) => (
        <Button
            fillMode="flat"
            size={size}
            svgIcon={icon}
            title={title}
            aria-label={title}
            aria-disabled={disabled ? true : undefined}
            disabled={disabled}
            className={`k-pager-nav ${extraClass}${disabled ? ' k-disabled' : ''}`}
            onClick={(e) => { e.preventDefault(); goToPage(page, e); }}
        />
    );

    return (
        <div
            className="k-pager k-grid-pager baasic-pager"
            role="application"
            aria-roledescription="pager"
            aria-label={`Page ${currentPage} of ${totalPages}`}
        >
            <div className="k-pager-numbers-wrap">
                {previousNext && navBtn(1, 'Go to the first page', caretAltToLeftIcon, currentPage === 1, 'k-pager-first')}
                {previousNext && navBtn(currentPage - 1, 'Go to the previous page', caretAltLeftIcon, currentPage === 1, 'k-pager-prev')}
                {showStartEllipsis && (
                    <Button
                        fillMode="flat"
                        size={size}
                        className="k-pager-nav k-pager-number"
                        title="More pages"
                        aria-label="More pages"
                        onClick={(e) => { e.preventDefault(); goToPage(chunkStart - 1, e); }}
                    >
                        ...
                    </Button>
                )}
                {numericButtons}
                {showEndEllipsis && (
                    <Button
                        fillMode="flat"
                        size={size}
                        className="k-pager-nav k-pager-number"
                        title="More pages"
                        aria-label="More pages"
                        onClick={(e) => { e.preventDefault(); goToPage(chunkEnd + 1, e); }}
                    >
                        ...
                    </Button>
                )}
                {previousNext && navBtn(currentPage + 1, 'Go to the next page', caretAltRightIcon, currentPage === totalPages, 'k-pager-next')}
                {previousNext && navBtn(totalPages, 'Go to the last page', caretAltToRightIcon, currentPage === totalPages, 'k-pager-last')}
            </div>
            {pageSizes && (
                <span className="k-pager-sizes">
                    <DropDownList
                        data={pageSizes}
                        value={take}
                        onChange={handlePageSizeChange}
                        ariaLabel="Page size"
                        size={size}
                        popupSettings={{ popupClass: 'is-pager-rpp', animate: false }}
                    />
                    <span>items per page</span>
                </span>
            )}
            {info && total > 0 && (
                <span className="k-pager-info">
                    {Math.min(skip + 1, total)} - {Math.min(skip + take, total)} of {total} items
                </span>
            )}
        </div>
    );
};

PagerTemplate.propTypes = {
    skip: PropTypes.number,
    take: PropTypes.number,
    total: PropTypes.number,
    buttonCount: PropTypes.number,
    info: PropTypes.bool,
    pageSizes: PropTypes.array,
    previousNext: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    onPageChange: PropTypes.func,
};

export default PagerTemplate;
